/**
 * Home promo + BDC shell from config-platform via BFF GET /v1/mobile/shell (same payload as native).
 * BFF caches `ezyhub/mobile_shell`; this module adds browser localStorage to avoid duplicate fetches per session.
 */

import type {
	PromoCarouselSlideConfig,
	PromoSlideAction,
	PromoSlidePlatform,
} from "@/app/data/default-promo-slides";
import { DEFAULT_PROMO_SLIDES } from "@/app/data/default-promo-slides";
import { getAuthConfig } from "@/config/auth";

const KNOWN_ACTIONS: ReadonlySet<PromoSlideAction> = new Set([
	"open-category",
	"open-troca-via-hub",
	"open-app-download",
]);

const KNOWN_PLATFORMS: ReadonlySet<PromoSlidePlatform> = new Set([
	"web",
	"ios",
	"android",
]);

const CACHE_KEY = "ezyhub_mobile_shell_v1";

let memoryJson: string | null = null;

function readLocalCache(): string | null {
	try {
		return localStorage.getItem(CACHE_KEY);
	} catch {
		return null;
	}
}

function writeLocalCache(json: string): void {
	try {
		localStorage.setItem(CACHE_KEY, json);
	} catch {
		/* ignore quota / private mode */
	}
}

function isRecord(v: unknown): v is Record<string, unknown> {
	return typeof v === "object" && v !== null;
}

const HEX = "#[0-9a-fA-F]{3,8}";
const GRADIENT_STOP = `${HEX}(?:\\s+\\d{1,3}%){0,2}`;
const GRADIENT_DIRECTION =
	"(?:\\d{1,3}deg|to\\s+(?:top|bottom|left|right)(?:\\s+(?:top|bottom|left|right))?)";
const BACKGROUND_STYLE_RE = new RegExp(
	`^(?:${HEX}|linear-gradient\\(\\s*(?:${GRADIENT_DIRECTION}\\s*,\\s*)?${GRADIENT_STOP}(?:\\s*,\\s*${GRADIENT_STOP}){1,7}\\s*\\))$`,
);

/** Accepts only a `#hex` color or a `linear-gradient` of `#hex` stops; anything else is dropped. */
export function sanitizeBackgroundStyle(value: unknown): string | undefined {
	if (typeof value !== "string") return undefined;
	const trimmed = value.trim();
	if (!trimmed || trimmed.length > 300) return undefined;
	return BACKGROUND_STYLE_RE.test(trimmed) ? trimmed : undefined;
}

function normalizeSlide(raw: unknown): PromoCarouselSlideConfig | null {
	if (!isRecord(raw)) return null;
	const id = typeof raw.id === "string" ? raw.id.trim() : "";
	const title = typeof raw.title === "string" ? raw.title.trim() : "";
	const category = typeof raw.category === "string" ? raw.category.trim() : "";
	const imageUrl = typeof raw.imageUrl === "string" ? raw.imageUrl.trim() : "";
	if (!id || !title || !category || !imageUrl) return null;
	const subtitle = typeof raw.subtitle === "string" ? raw.subtitle : "";
	const category_id =
		typeof raw.category_id === "string" ? raw.category_id : "";
	const backgroundColor =
		typeof raw.backgroundColor === "string" && raw.backgroundColor.trim()
			? raw.backgroundColor.trim()
			: "bg-gradient-to-r from-blue-500 to-cyan-500";
	const backgroundStyle = sanitizeBackgroundStyle(raw.backgroundStyle);
	const action =
		typeof raw.action === "string" &&
		KNOWN_ACTIONS.has(raw.action as PromoSlideAction)
			? (raw.action as PromoSlideAction)
			: undefined;
	const ctaLabel = typeof raw.ctaLabel === "string" ? raw.ctaLabel : undefined;
	const platforms = Array.isArray(raw.platforms)
		? (raw.platforms.filter(
				(p): p is PromoSlidePlatform =>
					typeof p === "string" && KNOWN_PLATFORMS.has(p as PromoSlidePlatform),
			) as PromoSlidePlatform[])
		: undefined;
	return {
		id,
		title,
		subtitle,
		category,
		category_id,
		imageUrl,
		backgroundColor,
		...(backgroundStyle ? { backgroundStyle } : {}),
		...(action ? { action } : {}),
		...(ctaLabel ? { ctaLabel } : {}),
		...(platforms && platforms.length > 0 ? { platforms } : {}),
	};
}

/** Parses `ezyhub/mobile_shell` JSON and returns promo slides, or default when the block omits slides. */
export function extractPromoSlidesFromMobileShellJson(
	json: string,
): PromoCarouselSlideConfig[] {
	let root: unknown;
	try {
		root = JSON.parse(json) as unknown;
	} catch {
		return [...DEFAULT_PROMO_SLIDES];
	}
	if (!isRecord(root)) return [...DEFAULT_PROMO_SLIDES];
	const screens = root.screens;
	if (!isRecord(screens)) return [...DEFAULT_PROMO_SLIDES];
	const home = screens.home;
	if (!isRecord(home)) return [...DEFAULT_PROMO_SLIDES];
	const blocks = home.blocks;
	if (!Array.isArray(blocks)) return [...DEFAULT_PROMO_SLIDES];
	const promo = blocks.find(
		(b) => isRecord(b) && b.type === "promo_carousel",
	) as Record<string, unknown> | undefined;
	if (!promo) return [...DEFAULT_PROMO_SLIDES];
	const props = isRecord(promo.props) ? promo.props : null;
	const slidesRaw = props?.slides;
	if (!Array.isArray(slidesRaw)) return [...DEFAULT_PROMO_SLIDES];
	if (slidesRaw.length === 0) return [];
	const out = slidesRaw
		.map(normalizeSlide)
		.filter((s): s is PromoCarouselSlideConfig => s !== null);
	return out.length > 0 ? out : [...DEFAULT_PROMO_SLIDES];
}

/**
 * Fetches shell from BFF (uses server-side config-platform cache), then caches JSON locally.
 * On network failure, uses in-memory or localStorage cache from a previous successful load.
 */
export async function loadPromoSlidesFromMobileShell(): Promise<
	PromoCarouselSlideConfig[]
> {
	const { baseUrl } = getAuthConfig();
	if (!baseUrl) {
		const cached = memoryJson ?? readLocalCache();
		return cached
			? extractPromoSlidesFromMobileShellJson(cached)
			: [...DEFAULT_PROMO_SLIDES];
	}
	const url = `${baseUrl.replace(/\/$/, "")}/v1/mobile/shell?platform=web`;
	try {
		const res = await fetch(url, {
			credentials: "include",
			headers: { Accept: "application/json" },
		});
		if (!res.ok) throw new Error(`shell ${res.status}`);
		const json = await res.text();
		if (!json.trim()) throw new Error("empty shell");
		memoryJson = json;
		writeLocalCache(json);
		return extractPromoSlidesFromMobileShellJson(json);
	} catch {
		const fallback = memoryJson ?? readLocalCache();
		if (fallback) return extractPromoSlidesFromMobileShellJson(fallback);
		return [...DEFAULT_PROMO_SLIDES];
	}
}
